import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaCircleNotch, FaClipboardList, FaSync, FaTimes } from 'react-icons/fa';
import LinkifiedText from './LinkifiedText';
import { formatDateTime } from '../utils/dates';
import { fetchStoredSummary, generateSummary, summaryNeedsUpdate } from '../utils/ticketSummary';
import './TicketSummaryOverlay.css';

// The ticket « Résumé » on an overlay (28/10), opened from the dashboard
// table: the stored summary at once when there is one (with a note and
// « Mettre à jour » when the conversation moved since, or when it is written
// in another language than the person's), generated when there is none.
// Rendered in a portal: the table's overflow container never clips it, and
// it never navigates away. Escape or a click outside closes it.
const TicketSummaryOverlay = ({ ticketId, subject, onClose }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState('loading'); // loading | generating | ready | error
  const closeRef = useRef(null);

  const generate = useCallback(async () => {
    setPhase('generating');
    try {
      setData(await generateSummary(ticketId));
      setPhase('ready');
    } catch (error) {
      setPhase('error');
    }
  }, [ticketId]);

  useEffect(() => {
    let alive = true;
    setPhase('loading');
    setData(null);
    fetchStoredSummary(ticketId)
      .then((stored) => {
        if (!alive) return;
        if (stored && stored.summary) {
          setData(stored);
          setPhase('ready');
        } else {
          generate();
        }
      })
      .catch(() => { if (alive) generate(); });
    return () => { alive = false; };
  }, [ticketId, generate]);

  // The parent re-renders often (hover tooltips): read its latest onClose
  // without re-running the effect (which would steal the focus each time).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', onKey);
    if (closeRef.current) closeRef.current.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const busy = phase === 'loading' || phase === 'generating';
  const languageName = (code) => (code ? t(`displayPrefs.language_${code}`, code.toUpperCase()) : '');
  const needsUpdate = phase === 'ready' && summaryNeedsUpdate(data);

  const overlay = (
    <div
      className="tso-overlay"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="tso-modal" role="dialog" aria-modal="true" aria-labelledby="tso-title">
        <div className="tso-header">
          <div className="tso-heading">
            <h3 id="tso-title"><FaClipboardList aria-hidden="true" /> {t('ticketSummaryOverlay.title', 'Ticket summary')}</h3>
            {subject && <span className="tso-subject" title={subject}>{subject}</span>}
          </div>
          <button
            ref={closeRef}
            type="button"
            className="tso-close"
            onClick={onClose}
            aria-label={t('ticketSummaryOverlay.close', 'Close')}
          >
            <FaTimes aria-hidden="true" />
          </button>
        </div>

        <div className="tso-content">
          {busy && (
            <div className="tso-loading" role="status">
              <FaCircleNotch className="tso-spinner" aria-hidden="true" />
              <span>
                {phase === 'loading'
                  ? t('ticketSummaryOverlay.loading', 'Loading the summary…')
                  : t('ticketSummaryOverlay.generating', 'Writing the summary…')}
              </span>
            </div>
          )}

          {phase === 'error' && (
            <div className="tso-error" role="alert">
              {t('ticketSummaryOverlay.error', 'The summary could not be written. Try again.')}
            </div>
          )}

          {phase === 'ready' && data && data.summary && (
            <>
              {data.generated_at && (
                <div className="tso-meta">
                  {data.generated_by
                    ? t('ticketSummaryOverlay.generatedOnBy', 'Generated on {{date}} · requested by {{name}}',
                      { date: formatDateTime(data.generated_at), name: data.generated_by })
                    : t('ticketSummaryOverlay.generatedOn', 'Generated on {{date}}',
                      { date: formatDateTime(data.generated_at) })}
                </div>
              )}
              {needsUpdate && (
                <div className="tso-note" role="note">
                  <div className="tso-note-text">
                    {data.stale && (
                      <span>{t('ticketSummaryOverlay.stale', 'New messages arrived since this summary was written.')}</span>
                    )}
                    {data.language_matches === false && data.language && (
                      <span>
                        {t('ticketSummaryOverlay.otherLanguage', 'This summary is in {{language}}; your preference is {{preferred}}.',
                          { language: languageName(data.language), preferred: languageName(data.preferred_language) })}
                      </span>
                    )}
                  </div>
                  <button type="button" className="tso-note-action" onClick={generate}>
                    {t('ticketSummaryOverlay.update', 'Update')}
                  </button>
                </div>
              )}
              <div className="tso-text">
                {data.summary.split('\n').map((line, index) => (
                  <p key={index}>{line ? <LinkifiedText text={line} /> : ' '}</p>
                ))}
              </div>
            </>
          )}

          {phase === 'ready' && (!data || !data.summary) && (
            <div className="tso-empty">{t('ticketSummaryOverlay.empty', 'Nothing to summarise yet.')}</div>
          )}
        </div>

        <div className="tso-actions">
          <button type="button" className="tso-button" onClick={onClose}>
            {t('ticketSummaryOverlay.close', 'Close')}
          </button>
          <button type="button" className="tso-button tso-button--primary" onClick={generate} disabled={busy}>
            {phase === 'generating'
              ? <><FaCircleNotch className="tso-spinner" aria-hidden="true" /> {t('ticketSummaryOverlay.generatingShort', 'Writing…')}</>
              : <><FaSync aria-hidden="true" /> {t('ticketSummaryOverlay.regenerate', 'Regenerate')}</>}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
};

export default TicketSummaryOverlay;
